import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { type ChordTypeId } from '@/music/chords';
import { IREAL_QUALITY_BY_TYPE } from '@/music/chart';
import type { IRealChord } from '@/music/ireal/types';
import {
  accidentalForKey,
  CHROMATIC_FLAT,
  conventionalTonicName,
  parseKey,
  pitchClassOf,
} from '@/music/notes';
import { TEMPO_MAX, TEMPO_MIN, useStore } from '@/state/store';
import { ChartView } from './ChartView';
import { MeasureEditor } from './MeasureEditor';
import { NumberField } from './NumberField';
import { ChordTypeSelect, NoteSelect } from './pickers';
import { ReharmSuggestions } from './ReharmSuggestions';
import { activeReharmLayer } from '@/music/score/reharm';

const TIME_SIGNATURES = ['4/4', '3/4', '2/4', '6/8', '5/4', '12/8', '2/2'] as const;
const CHORD_DURATIONS = [0.25, 0.5, 0.75, 1, 2, 3, 4, 5, 6, 7, 8] as const;

interface ChordDraft {
  root: string;
  chordType: ChordTypeId;
  bass: string;
  quality: string;
  duration: number;
  plainMajor: boolean;
  noChord: boolean;
}

interface ScoreFormEditorProps {
  /** Embed the controls beside the staff without rendering a second chord-chart canvas. */
  embedded?: boolean;
}

const EMPTY_CHORD: ChordDraft = {
  root: 'C',
  chordType: 'maj7',
  bass: '',
  quality: IREAL_QUALITY_BY_TYPE.maj7,
  duration: 4,
  plainMajor: false,
  noChord: false,
};

/** Form and harmony controls shared by Score Studio's staff editor. */
export function ScoreFormEditor({ embedded = false }: ScoreFormEditorProps = {}) {
  const chart = useStore((s) => s.chart);
  const score = useStore((s) => s.score);
  const selectedChordId = useStore((s) => s.selectedChordId);
  const selectedMeasureId = useStore((s) => s.selectedMeasureId);
  const insertionMeasureId = useStore((s) => s.insertionMeasureId);

  const setChartTitle = useStore((s) => s.setChartTitle);
  const setChartComposer = useStore((s) => s.setChartComposer);
  const setChartStyle = useStore((s) => s.setChartStyle);
  const setChartKey = useStore((s) => s.setChartKey);
  const tempo = useStore((s) => s.tempo);
  const setScoreTempo = useStore((s) => s.setScoreTempo);
  const setScoreTempoText = useStore((s) => s.setScoreTempoText);
  const setTimeSignature = useStore((s) => s.setTimeSignature);
  const transpose = useStore((s) => s.transpose);
  const selectChord = useStore((s) => s.selectChord);
  const selectMeasure = useStore((s) => s.selectMeasure);
  const insertMeasureAfter = useStore((s) => s.insertMeasureAfter);
  const addChord = useStore((s) => s.addChord);
  const updateChord = useStore((s) => s.updateChord);
  const removeChord = useStore((s) => s.removeChord);

  const [draft, setDraft] = useState<ChordDraft>(EMPTY_CHORD);
  useEffect(() => setDraft(EMPTY_CHORD), [score.id]);

  const selected = useMemo(
    () => findChord(chart.measures, selectedChordId),
    [chart, selectedChordId],
  );
  const selectedMeasure = useMemo(
    () => chart.measures.find((measure) => measure.id === selectedMeasureId) ?? null,
    [chart.measures, selectedMeasureId],
  );
  const reharmMeasureIds = useMemo(
    () => new Set(Object.keys(activeReharmLayer(score)?.overrides ?? {})),
    [score],
  );
  const selectedChord =
    selected && (!embedded || selected.measureId === selectedMeasureId) ? selected.chord : null;

  useEffect(() => {
    if (embedded && !selectedMeasure && chart.measures[0]) {
      selectChord(null);
      selectMeasure(chart.measures[0].id);
    }
  }, [chart.measures, embedded, selectChord, selectMeasure, selectedMeasure]);

  useEffect(() => {
    if (!selectedChord) return;
    const chordType = selectedChord.chordType ?? 'maj7';
    setDraft({
      root: selectedChord.root || 'C',
      chordType,
      bass: selectedChord.bass ?? '',
      quality: selectedChord.noChord ? IREAL_QUALITY_BY_TYPE[chordType] : selectedChord.quality,
      duration: selectedChord.beats,
      plainMajor: selectedChord.chordType === 'maj6' && selectedChord.quality === '',
      noChord: !!selectedChord.noChord,
    });
  }, [selectedChord]);

  const addFourBarPhrase = () => {
    const remainder = chart.measures.length % 4;
    const count = remainder === 0 ? 4 : 4 - remainder;
    let after = selectedMeasureId ?? insertionMeasureId ?? chart.measures.at(-1)?.id ?? null;
    selectChord(null);
    for (let i = 0; i < count; i++) {
      insertMeasureAfter(after);
      after = useStore.getState().selectedMeasureId;
    }
  };

  const handleAdd = () => {
    const quality = draft.quality.trim();
    addChord({
      root: draft.root,
      chordType: draft.chordType,
      beats: draft.duration,
      quality: quality === IREAL_QUALITY_BY_TYPE[draft.chordType] ? undefined : quality,
      noChord: draft.noChord,
      ...(!draft.noChord && draft.bass ? { bass: draft.bass } : {}),
    });
  };

  const handleUpdate = () => {
    if (!selectedChord) return;
    const quality = draft.quality.trim();
    updateChord(selectedChord.id, {
      root: draft.root,
      chordType: draft.chordType,
      bass: draft.noChord ? undefined : draft.bass || undefined,
      beats: draft.duration,
      quality: quality === IREAL_QUALITY_BY_TYPE[draft.chordType] ? undefined : quality,
      noChord: draft.noChord,
    });
  };

  const handleRemove = () => {
    if (selectedChord) removeChord(selectedChord.id);
  };

  return (
    <section
      className={`analysis-panel score-form-editor${embedded ? ' score-form-editor-embedded' : ''}`}
      aria-label="Score form and harmony editor"
      data-testid="score-form-editor"
    >
      <div
        className="row score-form-editor-head"
        style={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <div>
          <h2 style={{ margin: 0 }}>{embedded ? 'Score arrangement' : 'Form & harmony'}</h2>
          {embedded && (
            <span className="muted">
              {selectedMeasure
                ? `Editing bar ${chart.measures.indexOf(selectedMeasure) + 1}`
                : 'Click a bar in the notation to edit it'}
            </span>
          )}
        </div>
        <div className="row">
          <button
            type="button"
            className="btn btn-sm"
            data-testid="score-form-add-four-bars"
            onClick={addFourBarPhrase}
          >
            Add 4-bar phrase
          </button>
        </div>
      </div>

      <details className="score-form-metadata-details" open={!embedded}>
        <summary>Score details</summary>
        <div
          className="control-bar workspace-sheet-form chart-meta-bar"
          data-testid="score-form-metadata"
          style={{ marginTop: 8 }}
        >
          <FormField label="Title">
            <BufferedTextInput
              value={chart.title}
              onCommit={setChartTitle}
              ariaLabel="Score title"
              testId="score-form-title"
            />
          </FormField>
          <FormField label="Composer">
            <BufferedTextInput
              value={chart.composer ?? ''}
              onCommit={setChartComposer}
              ariaLabel="Score composer"
              testId="score-form-composer"
            />
          </FormField>
          <FormField label="Style">
            <BufferedTextInput
              value={chart.style ?? ''}
              onCommit={setChartStyle}
              ariaLabel="Score style"
              testId="score-form-style"
            />
          </FormField>
          <FormField label="Tempo words">
            <BufferedTextInput
              value={score.tempoText ?? ''}
              onCommit={setScoreTempoText}
              ariaLabel="Score tempo words"
              testId="score-form-tempo-words"
              placeholder="e.g. Presto"
            />
          </FormField>
          {!embedded && (
            <FormField label="Tempo">
              <NumberField
                min={TEMPO_MIN}
                max={TEMPO_MAX}
                value={tempo}
                onCommit={setScoreTempo}
                aria-label="Tempo (BPM)"
              />
            </FormField>
          )}
          <FormField label="Key">
            <NoteSelect
              value={keyTonic(chart.key)}
              onChange={(tonic) => setChartKey(keyLabel(tonic, keyMode(chart.key)))}
              aria-label="Score key"
            />
          </FormField>
          <FormField label="Mode">
            <select
              value={keyMode(chart.key)}
              aria-label="Score key mode"
              data-testid="score-form-key-mode"
              onChange={(event) =>
                setChartKey(keyLabel(keyTonic(chart.key), event.target.value as 'major' | 'minor'))
              }
            >
              <option value="major">Major</option>
              <option value="minor">Minor</option>
            </select>
          </FormField>
          {embedded && (
            <FormField label="Transpose">
              <div className="row" role="group" aria-label="Transpose complete score">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => transpose(-1)}
                  aria-label="Transpose score down a semitone"
                  title="Transpose the complete score down a semitone"
                >
                  −½
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => transpose(1)}
                  aria-label="Transpose score up a semitone"
                  title="Transpose the complete score up a semitone"
                >
                  +½
                </button>
              </div>
            </FormField>
          )}
          <FormField label="Time">
            <select
              value={`${chart.timeSignature[0]}/${chart.timeSignature[1]}`}
              onChange={(e) => {
                const [beats, unit] = e.target.value.split('/').map(Number);
                if (!setTimeSignature([beats, unit])) {
                  window.alert(
                    'That metre is too short for the existing bars. Shorten or rebar the content first.',
                  );
                }
              }}
              aria-label="Score time signature"
              data-testid="score-form-time"
            >
              {TIME_SIGNATURES.map((signature) => (
                <option key={signature} value={signature}>
                  {signature}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </details>

      {!embedded && (
        <div
          className="workspace-canvas chart-workspace-canvas"
          data-testid="score-form-chart"
          style={{ marginTop: 12 }}
        >
          <ChartView
            chart={chart}
            selectedChordId={selectedChordId}
            selectedMeasureId={selectedMeasureId}
            reharmMeasureIds={reharmMeasureIds}
            insertionMeasureId={insertionMeasureId}
            onSelectChord={(id) => {
              const found = findChord(chart.measures, id);
              if (!found) return;
              selectMeasure(found.measureId);
              selectChord(id);
            }}
            onSelectMeasure={(id) => {
              selectChord(null);
              selectMeasure(id);
            }}
          />
        </div>
      )}

      <div
        className="control-bar workspace-sheet-form"
        data-testid="score-form-chord-editor"
        style={{ marginTop: 12 }}
      >
        {embedded && (
          <FormField label="Chord in bar">
            <select
              value={selectedChord?.id ?? ''}
              aria-label="Chord in selected bar"
              disabled={!selectedMeasure}
              onChange={(event) => selectChord(event.target.value || null)}
            >
              <option value="">New chord…</option>
              {selectedMeasure?.chords.map((chord, index) => (
                <option key={chord.id} value={chord.id}>
                  {index + 1}. {chord.symbol || `${chord.root}${chord.quality}`}
                </option>
              ))}
            </select>
          </FormField>
        )}
        <FormField label="Root">
          <NoteSelect
            value={draft.root}
            onChange={(root) =>
              setDraft((current) => ({ ...current, root: spellForKey(root, chart.key) }))
            }
            aria-label="Chord root"
            disabled={draft.noChord}
          />
        </FormField>
        <FormField label="Chord">
          <ChordTypeSelect
            value={draft.chordType}
            onChange={(chordType) =>
              setDraft((current) => ({
                ...current,
                chordType,
                quality: IREAL_QUALITY_BY_TYPE[chordType],
                plainMajor: false,
              }))
            }
            aria-label="Chord type"
            disabled={draft.noChord}
          />
        </FormField>
        <FormField label="Slash bass">
          <NoteSelect
            value={draft.bass}
            onChange={(bass) =>
              setDraft((current) => ({ ...current, bass: spellForKey(bass, chart.key) }))
            }
            includeBlank
            blankLabel="none"
            aria-label="Slash bass"
            disabled={draft.noChord}
          />
        </FormField>
        <FormField label="Printed quality">
          <input
            className="text-input narrow"
            value={draft.quality}
            placeholder="e.g. ^7#11"
            aria-label="Printed chord quality"
            data-testid="score-form-chord-quality"
            disabled={draft.noChord}
            onChange={(e) =>
              setDraft((current) => ({
                ...current,
                quality: e.target.value,
                plainMajor: current.chordType === 'maj6' && e.target.value === '',
              }))
            }
          />
        </FormField>
        <FormField label="Duration">
          <select
            value={draft.duration}
            onChange={(e) =>
              setDraft((current) => ({ ...current, duration: Number(e.target.value) }))
            }
            aria-label="Chord duration"
            data-testid="score-form-chord-duration"
          >
            {CHORD_DURATIONS.map((duration) => (
              <option key={duration} value={duration}>
                {duration} beat{duration === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </FormField>
        <label className="switch" style={{ alignSelf: 'flex-end' }}>
          <input
            type="checkbox"
            checked={draft.plainMajor}
            disabled={draft.noChord || draft.chordType !== 'maj6'}
            onChange={(e) =>
              setDraft((current) => ({
                ...current,
                plainMajor: e.target.checked,
                quality: e.target.checked ? '' : IREAL_QUALITY_BY_TYPE[current.chordType],
              }))
            }
            aria-label="Plain major symbol"
            data-testid="score-form-plain-major"
          />
          Plain major symbol
        </label>
        <label className="switch" style={{ alignSelf: 'flex-end' }}>
          <input
            type="checkbox"
            checked={draft.noChord}
            onChange={(e) =>
              setDraft((current) => ({
                ...current,
                noChord: e.target.checked,
                plainMajor:
                  !e.target.checked && current.chordType === 'maj6' && current.quality === '',
              }))
            }
            aria-label="No chord span"
            data-testid="score-form-no-chord"
          />
          N.C.
        </label>
        <div className="row workspace-sheet-actions" style={{ alignSelf: 'flex-end' }}>
          <button
            type="button"
            className={`btn btn-sm${selectedChord ? '' : ' btn-primary'}`}
            data-testid="score-form-add-chord"
            onClick={handleAdd}
            disabled={embedded && !selectedMeasure}
          >
            {draft.noChord ? 'Add N.C.' : 'Add chord'}
          </button>
          <button
            type="button"
            className={`btn btn-sm${selectedChord ? ' btn-primary' : ''}`}
            data-testid="score-form-update-chord"
            onClick={handleUpdate}
            disabled={!selectedChord}
          >
            {draft.noChord ? 'Update N.C.' : 'Update chord'}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            data-testid="score-form-remove-chord"
            onClick={handleRemove}
            disabled={!selectedChord}
          >
            Remove
          </button>
        </div>
      </div>

      {selectedChord && !selectedChord.noChord && <ReharmSuggestions chordId={selectedChord.id} />}

      {selectedMeasureId ? (
        <div className="score-form-measure-wrap" style={{ marginTop: 12 }}>
          <MeasureEditor measureId={selectedMeasureId} compact={embedded} />
        </div>
      ) : (
        <p className="muted" style={{ marginBottom: 0 }}>
          {embedded
            ? 'Click a bar in the notation to edit its chords, section, repeats and barlines.'
            : 'Select a bar to edit its section, repeats and barlines.'}
        </p>
      )}
    </section>
  );
}

function findChord(
  measures: { id: string; chords: IRealChord[] }[],
  chordId: string | null,
): { chord: IRealChord; measureId: string } | null {
  if (!chordId) return null;
  for (const measure of measures) {
    const chord = measure.chords.find((candidate) => candidate.id === chordId);
    if (chord) return { chord, measureId: measure.id };
  }
  return null;
}

function keyTonic(key: string | undefined): string {
  if (!key) return 'C';
  return /^([A-G][#b]?)/.exec(key.trim())?.[1] ?? 'C';
}

function keyMode(key: string | undefined): 'major' | 'minor' {
  return /(?:^|\s)(?:minor|min|m)$/i.test(key?.trim() ?? '') ? 'minor' : 'major';
}

/** NoteSelect uses sharp pitch-class values internally. Key names are display
 * identities, so resolve the selected pitch class to a conventional tonic
 * before that label becomes the spelling context for authored harmony. */
function keyLabel(tonic: string, mode: 'major' | 'minor'): string {
  const spelledTonic = conventionalTonicName(pitchClassOf(tonic), mode);
  return mode === 'minor' ? `${spelledTonic} minor` : spelledTonic;
}

/** The picker groups enharmonic notes into one choice. Resolve that choice
 * through the score key so C minor authors E-flat/A-flat/D-flat rather than
 * their sharp aliases, while sharp keys retain the canonical sharp spelling. */
function spellForKey(note: string, key: string | undefined): string {
  if (!note || accidentalForKey(parseKey(key)) !== 'flat') return note;
  return CHROMATIC_FLAT[pitchClassOf(note)];
}

/** Keep spaces and partially typed metadata intact; one blur is one undo step. */
function BufferedTextInput({
  value,
  onCommit,
  ariaLabel,
  testId,
  placeholder,
}: {
  value: string;
  onCommit: (value: string) => void;
  ariaLabel: string;
  testId: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
  };

  return (
    <input
      className="text-input"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
      aria-label={ariaLabel}
      data-testid={testId}
      placeholder={placeholder}
    />
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="control-group">
      <span className="label">{label}</span>
      {children}
    </div>
  );
}
